// frame: hK08h @ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows.md §2.3 hK08h 页面-VIP购买
//
// VIP 主转化页(独立路由 /vip/buy)。当前 vip.tsx 内含 PurchaseView 二态切换,
// 该实现保留;本独立路由用于:
//   - .pen 中 `eovep renewBtn (7Jz0M)` 跳 hK08h 的语义对齐(directly enter buy)
//   - 营销链接 / 推送通知里直链购买(深链)
//   - 折叠后续可能要把 vip.tsx 的二态合并到这里
//
// 数据源:useVipPlans(/gapi/public/vip/plans) + useMyVip(/gapi/user/vip)
// 下单:POST /gapi/user/vip/orders(spec 见 docs/api-list/user/vip-purchase.md)

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CircleDollarSign, Crown, Smartphone, Wallet } from 'lucide-react'
import { Spinner, apiClient, ApiClientError, randomUUID, useCurrentUser, cn } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import {
  PLAN_TYPE_NAME,
  PLAN_TYPE_ORDER,
  useMyVip,
  useVipPlans,
  type VipPlan,
} from '../../_lib/vip-api'

const TIER_NAME: Record<string, string> = {
  bronze: '青铜VIP',
  silver: '白银VIP',
  gold: '黄金VIP',
  platinum: '铂金VIP',
}

const PAY_METHODS = [
  {
    id: 'coin' as const,
    label: '金币支付',
    Icon: CircleDollarSign,
    iconColor: '#F5B300',
    testid: 'hK08h-pay-coin',
  },
  {
    id: 'otcbtc' as const,
    label: '微信/支付宝',
    Icon: Smartphone,
    iconColor: '#22C55E',
    testid: 'hK08h-pay-otcbtc',
  },
] as const

type PayProvider = (typeof PAY_METHODS)[number]['id']

function durationLabel(days: number): string {
  if (days >= 365) return '/年'
  if (days >= 90) return '/季'
  if (days >= 30) return '/月'
  if (days === 7) return '/7天'
  return `/${days}天`
}

export function VipBuyPage() {
  const navigate = useNavigate()
  const { status: authStatus } = useCurrentUser()
  const isAuth = authStatus === 'authenticated'
  const { data: plansResp, isLoading: plansLoading } = useVipPlans()
  const { data: my } = useMyVip(isAuth)

  const plans = plansResp?.plans ?? []
  const [planId, setPlanId] = useState<string>('')
  const [provider, setProvider] = useState<PayProvider>('otcbtc')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [insufficientBalance, setInsufficientBalance] = useState(false)

  const selected = plans.find((p) => p.plan_id === planId)

  const onPay = async () => {
    if (!planId) {
      setError('请选择套餐')
      return
    }
    if (busy) return
    setBusy(true)
    setError(null)
    setInsufficientBalance(false)
    // 外链支付新开页面:必须在点击同步阶段开窗,await 响应后再 window.open 会被弹窗拦截
    const payWin = provider === 'otcbtc' ? window.open('', '_blank') : null
    try {
      const res = await apiClient.post<{ pay_url?: string; order_id?: string }>(
        '/gapi/user/vip/orders',
        { plan_id: planId, provider, idempotency_key: randomUUID() },
      )
      if (res.pay_url) {
        if (payWin) {
          payWin.opener = null
          payWin.location.href = res.pay_url
        } else {
          window.open(res.pay_url, '_blank', 'noopener')
        }
      } else {
        payWin?.close()
      }
      if (res.order_id) {
        navigate(`/recharge/callback?order_id=${res.order_id}`)
      } else {
        navigate('/orders')
      }
    } catch (e) {
      payWin?.close()
      if (e instanceof ApiClientError && e.code === 'INSUFFICIENT_BALANCE') {
        setInsufficientBalance(true)
        setError('金币余额不足')
      } else if (e instanceof ApiClientError && e.code === 'WALLET_UNAVAILABLE') {
        setError('支付暂时不可用,请稍后再试')
      } else {
        setError(e instanceof Error ? e.message : '下单失败')
      }
    } finally {
      setBusy(false)
    }
  }

  const currentTierLabel =
    my && my.effective_tier ? (TIER_NAME[my.effective_tier] ?? '会员') : '免费用户'

  // Footer 合计文案
  const totalLabel = (() => {
    if (!selected) return null
    if (provider === 'coin') return `${selected.price_coin} 金币`
    return `¥${(selected.price_cny_cents / 100).toFixed(2)}`
  })()

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid="hK08h" className="flex flex-1 flex-col">
          <TopBar
            title="升级会员"
            fallback="/vip"
          />

          {/* CurrentPlan 胶囊 */}
          <div className="px-6 pt-3">
            <div className="flex items-center gap-2 rounded-2xl bg-[#F6F7F8] px-4 py-3 text-[13px] text-[#9CA3AF]">
              <Wallet size={16} className="shrink-0 text-[#9CA3AF]" />
              <span>
                当前: {currentTierLabel}
                {my?.effective_expires_at && (
                  <span className="ml-2 text-[11px] text-gray-400">
                    到期 {my.effective_expires_at.slice(0, 10)}
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* pb 须盖过 fixed Footer 实高(~160px:合计+按钮+协议+safe-area),否则支付方式末行被遮挡 */}
          <main className="flex-1 px-6 pt-4 pb-48">
            {plansLoading ? (
              <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
            ) : plans.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-[#9CA3AF]">暂无可购套餐</p>
            ) : (
              // 按 plan_type 分 3 组(spec 2026-06-10 §5):用户/论坛创作/约爱创作
              PLAN_TYPE_ORDER.map((pt) => {
                const group = plans.filter((p) => p.plan_type === pt)
                if (group.length === 0) return null
                return (
                  <section key={pt} data-testid={`plan-group-${pt}`}>
                    <p className="pt-1 pb-2 text-[14px] font-semibold text-[#1A1A1A]">
                      {PLAN_TYPE_NAME[pt]}
                    </p>
                    <ul className="flex flex-col gap-3 pb-3">
                      {group.map((p, i) => (
                        <PlanCard
                          key={p.plan_id}
                          plan={p}
                          selected={planId === p.plan_id}
                          onSelect={() => setPlanId(p.plan_id)}
                          // user 组沿用旧 testid(L3 既有用例);创作组用 plan-<type>-<i>
                          testid={
                            pt === 'user'
                              ? (['CJu9z', 'wucNW', '6yXQm', 'SSfkh'][i] ?? `hK08h-plan-${p.plan_id}`)
                              : `hK08h-plan-${p.plan_id}`
                          }
                        />
                      ))}
                    </ul>
                  </section>
                )
              })
            )}

            {/* 支付方式 */}
            <p className="mt-5 pb-2 text-[14px] font-semibold text-[#1A1A1A]">支付方式</p>
            <ul role="radiogroup" className="flex flex-col rounded-2xl bg-[#F6F7F8] overflow-hidden">
              {PAY_METHODS.map((m, idx) => (
                <li key={m.id} className={idx < PAY_METHODS.length - 1 ? 'border-b border-[#E5E7EB]' : ''}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={provider === m.id}
                    data-testid={m.testid}
                    onClick={() => setProvider(m.id)}
                    className="flex h-14 w-full items-center gap-3 px-4 text-left"
                  >
                    <m.Icon size={20} color={m.iconColor} />
                    <span className="flex-1 text-[14px] text-[#1A1A1A]">{m.label}</span>
                    {/* radio 选中=22px 主色实心圆+白点;未选=22px 灰色描边 */}
                    <span
                      className={cn(
                        'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full',
                        provider === m.id
                          ? 'bg-[#19caad]'
                          : 'border-2 border-[#D1D5DB] bg-transparent',
                      )}
                    >
                      {provider === m.id && (
                        <span className="h-[10px] w-[10px] rounded-full bg-white" />
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {error && (
              <p className="mt-3 text-[12px] text-[#FF6B6B]">
                {error}
                {insufficientBalance && (
                  <>
                    {' '}
                    <Link
                      to="/wallet/recharge"
                      data-testid="hK08h-recharge-link"
                      className="text-[#19caad] underline"
                    >
                      去充值
                    </Link>
                  </>
                )}
              </p>
            )}
          </main>

          {/* Footer */}
          <div
            className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] bg-white px-6 pt-3"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
          >
            {/* 合计价 */}
            <div className="mb-2 text-center">
              {totalLabel ? (
                <span className="font-['Bricolage_Grotesque',sans-serif] text-[24px] font-bold text-[#1A1A1A]">
                  {totalLabel}
                </span>
              ) : (
                <span className="text-[14px] text-[#9CA3AF]">请选择套餐</span>
              )}
            </div>

            <button
              type="button"
              data-testid="hK08h-pay-btn"
              onClick={onPay}
              disabled={busy || !planId}
              className="h-12 w-full rounded-xl bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#17b89c] disabled:opacity-50"
            >
              {busy ? '处理中…' : '立即支付'}
            </button>
            <p className="mt-2 text-center text-[12px] text-[#9CA3AF]">
              支付即同意《会员服务协议》
            </p>
          </div>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

function PlanCard({
  plan,
  selected,
  onSelect,
  testid,
}: {
  plan: VipPlan
  selected: boolean
  onSelect: () => void
  testid: string
}) {
  return (
    <li>
      <button
        type="button"
        data-testid={testid}
        onClick={onSelect}
        className={cn(
          'flex w-full flex-col gap-1 rounded-2xl border p-4 text-left',
          selected
            ? 'border-2 border-[#19caad] bg-[#F0FDFA]'
            : 'border border-[#F3F4F6] bg-white',
        )}
      >
        {/* 首行: 图标 + 名称 + 推荐 badge */}
        <div className="flex items-center gap-1.5">
          <Crown size={20} className="shrink-0 text-[#DAA520]" />
          <span className="flex-1 text-[17px] font-semibold text-[#1A1A1A]">{plan.name}</span>
          {plan.tag === 'hot' && (
            <span className="rounded-lg bg-[#19caad] px-2 py-0.5 text-[10px] font-semibold text-white">
              推荐
            </span>
          )}
        </div>
        {/* 价格行 */}
        <p className="font-['Bricolage_Grotesque',sans-serif] text-[28px] font-bold tabular-nums text-[#1A1A1A]">
          ¥{(plan.price_cny_cents / 100).toFixed(2)}
          <span className="ml-1 text-[14px] font-normal text-[#9CA3AF]">
            {durationLabel(plan.duration_days)}
          </span>
        </p>
        {/* 描述行(仅有 description 时展示;VipPlan 暂无 description 字段,预留) */}
      </button>
    </li>
  )
}
